/**
 * Feedback page: form to submit feedback message.
 */

import { useState } from "react";
import { Layout } from "../../components/layout/index.ts";
import { haptic } from "../../lib/telegram.ts";
import { useSendFeedback } from "./hooks";
import { getErrorMessage } from "../../lib/graphql.ts";

export function FeedbackPage() {
  const [text, setText] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { sendFeedback, isLoading: isSubmitting } = useSendFeedback();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    haptic("light");
    setError(null);
    try {
      await sendFeedback({
        text: trimmed,
        authorName: authorName.trim() || undefined,
      });
      setSubmitted(true);
      setText("");
      setAuthorName("");
    } catch (err) {
      setError(getErrorMessage(err, "Не удалось отправить сообщение"));
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-8 xs:p-10 text-center">
          <h1 className="text-xl xs:text-2xl font-bold text-slate-800 mb-2">
            Спасибо!
          </h1>
          <p className="text-slate-600 text-sm">
            Мы получили ваше сообщение и ответим в ближайшее время.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-6 xs:p-8">
        <h1 className="text-xl font-bold text-slate-900 mb-1">
          Обратная связь
        </h1>
        <p className="text-slate-500 text-sm mb-4">
          Оставьте сообщение — мы ответим вам
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="feedback-name"
              className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1"
            >
              Ваше имя (необязательно)
            </label>
            <input
              id="feedback-name"
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="Как к вам обращаться"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30 focus:border-[var(--color-accent)]"
            />
          </div>
          <div>
            <label
              htmlFor="feedback-text"
              className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1"
            >
              Сообщение
            </label>
            <textarea
              id="feedback-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Опишите вопрос или предложение..."
              rows={5}
              required
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30 focus:border-[var(--color-accent)] resize-none"
            />
          </div>
          {error && (
            <p className="text-sm text-amber-600">{error}</p>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !text.trim()}
            className="btn-primary w-full"
          >
            {isSubmitting ? "Отправка…" : "Отправить"}
          </button>
        </form>
      </div>
    </Layout>
  );
}
